import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
  ICompteur,
  ICompteurFilter,
  ICompteurRaz,
  ICompteurRazRequest,
  ICompteurRazStep,
  ICompteurSchemaPref,
  CompteurNature,
  CompteurSegment,
} from "../models/compteur";
import CompteurService from "../services/CompteurService";
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";
import { useUsersStore } from "./users.store";
import type { LoadingStep } from "../components/Loading/LoadingSteps.vue";
import i18n from "@/i18n";

// U03 — Libellés FR des étapes de RAZ (panneau de progression, cf. LoadingSteps.vue déjà utilisé
// sur PlanMonte). Une étape = une Nature recalculée indépendamment côté serveur.
const RAZ_STEP_LABELS: Record<CompteurNature, string> = {
  Besoin: "Commandes",
  Dispo: "Dispatchs / orientations",
  DispoAttente: i18n.global.t('animal.pendingSamplings'),
  Baseline: i18n.global.t('mountingPlan.planned'),
  ParcPreleve: i18n.global.t('animal.sampledPark'),
  Stock: "Stock",
};

// Lot9 / E30 — Store des compteurs de production (liaison données + temps réel).
// D11 : le store est la SEULE couche qui porte la sémantique des compteurs ;
// la vue pioche une valeur prête à afficher via `getter.get(col.key)`.
//
// Convention de clé de colonne — IDENTIQUE à CompteursView (axe X du pivot) :
//   colKey = `${schemaId}__${volume}__${concentration}`
//   colonne de base d'un schéma (sans volume/concentration) = `${schemaId}____`.
// Les getters « nombre » renvoient Map<colKey, number> (drop-in pour byCol).
export const useCompteurStore = defineStore("compteurs", () => {
  const datas = ref<ICompteur[]>([]);
  const isLoading = ref(false);
  const loadingMessage = ref("Chargement en cours…"); // affiché par la vue via element-loading-text pendant isLoading
  const version = ref(0); // incrémenté à chaque push SignalR, pour déclencher le flash dans la vue
  // Incrémenté uniquement quand `load()` remplace TOUT `datas` (changement de jour OU fin de RAZ/
  // changement de plan de monte) — contrairement à `version` (poussé aussi à chaque upsert unitaire
  // SignalR), sert de signal pour réinitialiser un état dérivé qui ne doit repartir de zéro qu'à un
  // rechargement complet (ex. repli automatique des blocs vides dans CompteursView.vue).
  const loadVersion = ref(0);
  // Jour actuellement chargé (filter.date de load(), "YYYY-MM-DD") : sert à ignorer les push
  // SignalR (upsertLocal) d'une ligne d'un AUTRE jour (ex. commande créée pour demain pendant
  // qu'on regarde aujourd'hui) — sans ça, `datas` se pollue avec des lignes hors du jour affiché
  // (ex. badge "verrats réservés" qui compte tout `datas` sans re-filtrer par date).
  const loadedDate = ref<string | null>(null);

  // U02 : préférences d'affichage des schémas du centre (sélection + ordre).
  const schemaPrefs = ref<ICompteurSchemaPref[]>([]);
  // U05 : potentiel doses par verrat (restant à orienter), chargé à la demande + caché.
  const boarPotentials = ref<Record<number, number>>({});
  // U03 : dernier RAZ enregistré pour le jour affiché (et donc le plan de monte actuellement
  // associé au « Prévu ») — null si aucun RAZ n'a encore été fait ce jour-là.
  const latestRaz = ref<ICompteurRaz | null>(null);

  // U03 : avancement du RAZ en cours (une étape par Nature, recalculées en parallèle côté serveur)
  // — panneau de progression (LoadingSteps), poussé en temps réel par SignalR au fil de l'eau.
  const razSteps = ref<ICompteurRazStep[]>([]);
  let razDate: string | undefined; // jour du RAZ suivi, pour recharger une fois toutes les étapes terminées
  let razReloaded = false; // évite de recharger plusieurs fois si 2 étapes se terminent presque en même temps

  const razAllStepsSettled = computed(
    () => razSteps.value.length > 0 && razSteps.value.every((s) => s.status === "Done" || s.status === "Error")
  );
  const razInProgress = computed(() => razSteps.value.length > 0 && !razAllStepsSettled.value);
  const razLoadingSteps = computed<LoadingStep[]>(() =>
    razSteps.value.map((s) => ({
      key: String(s.id),
      label: RAZ_STEP_LABELS[s.nature] ?? s.nature,
      status:
        s.status === "Pending" ? "pending" : s.status === "InProgress" ? "loading" : s.status === "Done" ? "done" : "error",
    }))
  );

  // ---- clés de colonne (alignées sur la vue) ----
  const colKey = (c: ICompteur) =>
    `${c.schemaId ?? ""}__${c.volume ?? ""}__${c.concentration ?? ""}`;
  // Colonne de base du schéma (les compteurs « par verrat/schéma » — Parc — n'ont
  // pas de volume/concentration → ils tombent sur la colonne de base).
  const baseColKey = (c: ICompteur) => `${c.schemaId ?? ""}____`;
  // Clé externe des getters « fins » : segment × conditionnement.
  const segCondKey = (c: ICompteur) =>
    `${c.segment ?? "Mono"}__${c.conditionnementId ?? ""}`;

  // ---- index de base : lignes groupées par (nature, segment) ----
  const byNatureSegment = computed(() => {
    const m = new Map<string, ICompteur[]>();
    for (const c of datas.value) {
      const k = `${c.nature}|${c.segment ?? "Mono"}`;
      let bucket = m.get(k);
      if (!bucket) {
        bucket = [];
        m.set(k, bucket);
      }
      bucket.push(c);
    }
    return m;
  });

  const linesOf = (
    nature: CompteurNature,
    segment: CompteurSegment = "Mono"
  ): ICompteur[] => byNatureSegment.value.get(`${nature}|${segment}`) ?? [];

  // Toutes les lignes d'une nature, tous segments confondus.
  const linesOfNature = (nature: CompteurNature): ICompteur[] =>
    datas.value.filter((c) => c.nature === nature);

  // ===================== HELPERS D'AGRÉGATION =====================
  // Tous renvoient Map<clé, number> (drop-in pour les `byCol` de la vue).

  /** Σ quantite par clé. */
  function sumBy(
    lines: ICompteur[],
    keyOf: (c: ICompteur) => string
  ): Map<string, number> {
    const acc = new Map<string, number>();
    for (const c of lines) acc.set(keyOf(c), (acc.get(keyOf(c)) ?? 0) + c.quantite);
    return acc;
  }

  /** COUNT(DISTINCT boarId) par clé. */
  function countDistinctBoar(
    lines: ICompteur[],
    keyOf: (c: ICompteur) => string
  ): Map<string, number> {
    const acc = new Map<string, Set<number>>();
    for (const c of lines) {
      if (c.boarId == null) continue;
      const k = keyOf(c);
      let set = acc.get(k);
      if (!set) {
        set = new Set<number>();
        acc.set(k, set);
      }
      set.add(c.boarId);
    }
    return new Map([...acc].map(([k, v]) => [k, v.size]));
  }

  /** Σ nbBoars par clé (verrats orientés hétéro, pré-calculé backend U06). */
  function sumNbBoars(
    lines: ICompteur[],
    keyOf: (c: ICompteur) => string
  ): Map<string, number> {
    const acc = new Map<string, number>();
    for (const c of lines)
      acc.set(keyOf(c), (acc.get(keyOf(c)) ?? 0) + (c.nbBoars ?? 0));
    return acc;
  }

  // ---- helpers « imbriqués » : Map<cléExterne, Map<colKey, number>> ----
  // Pour les getters fins (1 sous-map par conditionnement / groupe).

  /** Σ quantite, groupé par (clé externe → colonne). */
  function nestedSumByCol(
    lines: ICompteur[],
    outerKeyOf: (c: ICompteur) => string,
    innerKeyOf: (c: ICompteur) => string = colKey
  ): Map<string, Map<string, number>> {
    const acc = new Map<string, Map<string, number>>();
    for (const c of lines) {
      const ok = outerKeyOf(c);
      let inner = acc.get(ok);
      if (!inner) {
        inner = new Map<string, number>();
        acc.set(ok, inner);
      }
      const ik = innerKeyOf(c);
      inner.set(ik, (inner.get(ik) ?? 0) + c.quantite);
    }
    return acc;
  }

  /** COUNT(DISTINCT boarId), groupé par (clé externe → colonne). */
  function nestedDistinctBoarByCol(
    lines: ICompteur[],
    outerKeyOf: (c: ICompteur) => string,
    innerKeyOf: (c: ICompteur) => string
  ): Map<string, Map<string, number>> {
    const acc = new Map<string, Map<string, Set<number>>>();
    for (const c of lines) {
      if (c.boarId == null) continue;
      const ok = outerKeyOf(c);
      let inner = acc.get(ok);
      if (!inner) {
        inner = new Map<string, Set<number>>();
        acc.set(ok, inner);
      }
      const ik = innerKeyOf(c);
      let set = inner.get(ik);
      if (!set) {
        set = new Set<number>();
        inner.set(ik, set);
      }
      set.add(c.boarId);
    }
    return new Map(
      [...acc].map(([ok, inner]) => [
        ok,
        new Map([...inner].map(([ik, s]) => [ik, s.size])),
      ])
    );
  }

  // ===================== GETTERS PAR BLOC =====================
  // Chaque getter = une ligne du tableau, prête à brancher sur `byCol`.

  // --- BLOC JAUNE : disponible en attente (U07) ---
  // « Dispo mono (attente) » = Σ doses ; « Nb. Ver.diff. » = distinct boarId.
  const attenteDosesMono = computed(() => sumBy(linesOf("DispoAttente", "Mono"), colKey));
  const attenteVerratsMono = computed(() =>
    countDistinctBoar(linesOf("DispoAttente", "Mono"), colKey)
  );
  const attenteDosesHeteroStd = computed(() =>
    sumBy(linesOf("DispoAttente", "HeteroStandard"), colKey)
  );
  const attenteDosesHeteroArm = computed(() =>
    sumBy(linesOf("DispoAttente", "HeteroArmada"), colKey)
  );

  // --- BLOCS HÉTÉRO : orienté mais PAS ENCORE conditionné (U06) — lignes « Nbre de doses /
  // Nb verrats orientés ». Distinct des lignes déjà conditionnées (`conditionnementId` renseigné,
  // cf. `orienteHeteroDosesByCond`/« HS - Selfix ») : un dispatch peut être marqué "orienté" (segment
  // Hetero*) avant même la création du lot hétéro final et son conditionnement — pas de double compte.
  const nonConditionne = (c: ICompteur) => c.conditionnementId == null;
  // Discriminant lot créé/pas créé : `nbBoars`, renseigné par la projection pour le seul blend
  // hétéro final (nombre de contributeurs) et laissé null à l'étape « orienté sans lot ». `boarId`
  // donnerait le même résultat en miroir (null sur le blend, renseigné sinon), mais nbBoars est le
  // champ posé exprès pour marquer le lot final. Défini ici (avant les getters `oriente*`) car les
  // deux familles de getters (total « oriente* » ci-dessous et « attenteLots* » plus bas) en dépendent.
  const lotHeteroCree = (c: ICompteur) => c.nbBoars != null;
  // TOTAL utilisé pour le NET besoin/dispo du bandeau de section Hétéro Std/Arm (Mantis 0028304) :
  // inclut sciemment les doses déjà regroupées dans un lot créé non conditionné (elles couvrent
  // toujours le besoin) — ne pas restreindre à `!lotHeteroCree`, sous peine de faire réapparaître un
  // besoin déjà couvert dans le bandeau dès qu'un lot est constitué.
  const orienteDosesHeteroStd = computed(() =>
    sumBy(linesOf("Dispo", "HeteroStandard").filter(nonConditionne), colKey)
  );
  const orienteVerratsHeteroStd = computed(() =>
    countDistinctBoar(linesOf("Dispo", "HeteroStandard").filter(nonConditionne), colKey)
  );
  const orienteDosesHeteroArm = computed(() =>
    sumBy(linesOf("Dispo", "HeteroArmada").filter(nonConditionne), colKey)
  );
  const orienteVerratsHeteroArm = computed(() =>
    countDistinctBoar(linesOf("Dispo", "HeteroArmada").filter(nonConditionne), colKey)
  );

  // Décision produit 2026-08-27 : une fois les doses orientées passées dans un lot hétéro
  // (`lotHeteroCree`), elles ne doivent plus alimenter l'AFFICHAGE de la ligne « Nbre de doses
  // orientées » — elles basculent visuellement dans « Dispo Hétéro Std/Arm (att.) »
  // (`attenteLotsHetero*` ci-dessous), sinon les deux lignes affichent le même total en double dès
  // qu'un lot est constitué (constat terrain : 45 doses orientées en hétéro armada -> lot constitué
  // avec ces 45 doses -> "Nbre de doses orientées" restait à 45 au lieu de retomber à 0).
  // Distinct de `orienteDosesHeteroStd/Arm` ci-dessus (total complet, conservé pour le bandeau de
  // section — cf. commentaire sur ces getters) : seul l'AFFICHAGE de la ligne « orientées » se
  // restreint aux doses PAS ENCORE dans un lot.
  const orienteDosesHeteroStdAvantLot = computed(() =>
    sumBy(linesOf("Dispo", "HeteroStandard").filter(nonConditionne).filter((c) => !lotHeteroCree(c)), colKey)
  );
  const orienteDosesHeteroArmAvantLot = computed(() =>
    sumBy(linesOf("Dispo", "HeteroArmada").filter(nonConditionne).filter((c) => !lotHeteroCree(c)), colKey)
  );

  // --- BLOC JAUNE, lignes hétéro : LOT hétéro créé mais pas encore conditionné.
  // Règle énoncée par le modèle du tableau (Documents/Lot9/Haut tableau compteurs.png, capture du
  // fichier Excel « modèle 2 »), en face de la ligne « Dispo Hétéro Std (attente) » :
  //     « Cumul doses des Lots en attentes [Hétéro Std]. Lot créé mais non conditionné. »
  // Mantis 0028300 : ces deux lignes comptaient plus que ça. Le wiki les donnait encore comme non
  // faites (E30-F01-U01-Attente.md, U01-ATT-B3 « dépend QA2 ») et aucun REQ ne les couvrait — le
  // modèle est donc la source de la règle.
  // Sous-ensemble STRICT des getters `oriente*` ci-dessus : ceux-là comptent toute orientation vers
  // l'hétérospermie, y compris une fabrication mono marquée "H" pour laquelle AUCUN lot n'existe
  // encore (CompteurProjectionService : `isOrientedNotConditioned`). Le bandeau de section garde ce
  // total complet (couverture du besoin), mais les lignes jaunes « Dispo Hétéro Std/Arm (att.) » ne
  // doivent porter que les doses de lots RÉELLEMENT CRÉÉS et non conditionnés.
  const attenteLotsHeteroStd = computed(() =>
    sumBy(linesOf("Dispo", "HeteroStandard").filter(nonConditionne).filter(lotHeteroCree), colKey)
  );
  const attenteLotsHeteroArm = computed(() =>
    sumBy(linesOf("Dispo", "HeteroArmada").filter(nonConditionne).filter(lotHeteroCree), colKey)
  );

  // --- BLOC PARC (U08) : counts/potentiel par schéma → colonne de base ---
  const baselineLines = computed(() => linesOf("Baseline", "Mono"));
  const parcNbVerratPlan = computed(() =>
    countDistinctBoar(baselineLines.value, baseColKey)
  ); // « Nb Verrats PDM »
  const parcNbVerratPreleve = computed(() =>
    countDistinctBoar(linesOf("ParcPreleve", "Mono"), baseColKey)
  ); // « Nb Prélèvements »
  const parcPotentielCommercial = computed(() =>
    sumBy(baselineLines.value.filter((c) => c.isCommercial === true), baseColKey)
  ); // « Potentiel Doses C »
  const parcNbVerratCommercial = computed(() =>
    countDistinctBoar(
      baselineLines.value.filter((c) => c.isCommercial === true),
      baseColKey
    )
  ); // « Nb Verrat PDM Commer »
  const parcNbVerratNonCommercial = computed(() =>
    countDistinctBoar(
      baselineLines.value.filter((c) => c.isCommercial === false),
      baseColKey
    )
  ); // « Nb Verrat PDM NC »

  // --- BLOC STOCK (U09) : doses en stock par colonne, par segment ---
  const stockDosesMono = computed(() => sumBy(linesOf("Stock", "Mono"), colKey));
  const stockDosesHeteroStd = computed(() =>
    sumBy(linesOf("Stock", "HeteroStandard"), colKey)
  );
  const stockDosesHeteroArm = computed(() =>
    sumBy(linesOf("Stock", "HeteroArmada"), colKey)
  );

  // NB : bloc GRIS mono (net Besoin/Dispo par conditionnement × groupe × réservé)
  // reste agrégé dans la vue (logique riche cond/créneau/réservé) — cf. D11 / wiki.

  // ===================== GETTERS FINS (maille conditionnement / groupe) =====================
  // Structure imbriquée Map<cléExterne, Map<colKey, number>> : la vue lit
  //   getter.get(cléExterne)?.get(col.key).
  // La vue résout ses libellés de ligne (« HS - Selfix », « G1 »…) vers la clé
  // externe correspondante (voir E30-Compteur-Store-Getters).

  // --- STOCK par (segment, conditionnement) — clé externe `${segment}__${condId}` ---
  // Alimente les lignes Stock « Selfix / HS - Selfix / AS - Selfix … ».
  const stockDosesByCond = computed(() =>
    nestedSumByCol(linesOfNature("Stock"), segCondKey)
  );

  // --- HÉTÉRO conditionné par (segment, conditionnement) — clé externe `${segment}__${condId}` ---
  // Alimente les lignes hétéro par conditionnement (« HS - Selfix »...). Exclut les lignes "orientées
  // mais pas encore conditionnées" (`conditionnementId` null, cf. `orienteDosesHetero*` ci-dessus) :
  // une même dose ne doit compter que dans un seul des deux blocs, jamais les deux.
  const orienteHeteroDosesByCond = computed(() =>
    nestedSumByCol(
      linesOfNature("Dispo").filter((c) => (c.segment ?? "Mono") !== "Mono" && c.conditionnementId != null),
      segCondKey
    )
  );

  // --- PARC : nb verrat par GROUPE — clé externe = `creneau` ("G{n}" du plan de monte) ---
  // Alimente les lignes Parc « G1 / G2 / G3 ». Colonne = colonne de base du schéma.
  // ⚠️ Le n est celui du plan de monte (Baseline.Creneau), pas la renumérotation
  // triée du bloc gris de la vue — à faire correspondre côté vue.
  const parcNbVerratByGroupe = computed(() =>
    nestedDistinctBoarByCol(baselineLines.value, (c) => c.creneau ?? "", baseColKey)
  );

  // ⚠️ HÉTÉRO par GROUPE non exposé : Dispo.Creneau = null côté backend (pas de
  // donnée « groupe » sur l'orienté) → impossible sans évolution de la projection.

  // ===================== U02 : PRÉFÉRENCES SCHÉMAS =====================
  // Ids des schémas visibles, dans l'ordre d'affichage choisi (la vue filtre/ordonne
  // ses colonnes avec — cf. U02-F3). Prefs vides = pas de filtre (la vue retombe sur
  // les schémas configurés / présents dans les données).
  const orderedVisibleSchemaIds = computed(() =>
    [...schemaPrefs.value]
      .filter((p) => p.visible)
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map((p) => p.parameterId)
  );

  async function loadSchemaPrefs() {
    const result = await CompteurService.getSchemaPrefs(currentCenterId());
    if (!result.error) schemaPrefs.value = (result.data as ICompteurSchemaPref[]) ?? [];
    return result;
  }

  /** Remplace toutes les prefs du centre (sélection + ordre) et resynchronise l'état local. */
  async function saveSchemaPrefs(prefs: ICompteurSchemaPref[]) {
    const result = await CompteurService.saveSchemaPrefs(prefs, currentCenterId());
    if (!result.error) schemaPrefs.value = (result.data as ICompteurSchemaPref[]) ?? [];
    return result;
  }

  // ===================== CHARGEMENT + TEMPS RÉEL =====================
  // Le centre est fourni par le front (UserStore.currentCenter) — plus de résolution
  // physique côté serveur (le mscenter n'a plus de « centre courant » par settings).
  const currentCenterId = () => useUsersStore().currentCenter?.id;

  async function load(filter?: ICompteurFilter) {
    loadedDate.value = filter?.date ?? null;
    isLoading.value = true;
    // `finally` requis (cf. raz() ci-dessous, même garde) : si quoi que ce soit lève ici (aujourd'hui
    // aucune des deux méthodes ne rejette sa promesse, mais une régression future ne doit pas laisser
    // isLoading bloqué à `true` pour toujours — le spinner resterait affiché jusqu'au prochain F5).
    try {
      const [result] = await Promise.all([
        CompteurService.search(filter, currentCenterId()),
        loadLatestRaz(filter?.date),
      ]);
      if (!result.error) {
        datas.value = (result.data as ICompteur[]) ?? [];
        loadVersion.value++;
      }
      return result;
    } finally {
      isLoading.value = false;
    }
  }

  /** U03 : dernier RAZ du jour (plan de monte actuellement associé au « Prévu »). */
  async function loadLatestRaz(date?: string) {
    const result = await CompteurService.getLatestRaz(date, currentCenterId());
    if (!result.error) latestRaz.value = (result.data as ICompteurRaz | null) ?? null;
    return result;
  }

  /** Pousse un RAZ reçu en temps réel (SignalR, un autre utilisateur a changé le plan de monte du
   * jour affiché) — même garde-fou de date que `upsertLocal` : ignore un RAZ d'un autre jour, sinon
   * le badge "Plan de monte" de cet utilisateur passif resterait bloqué sur l'ancien plan tant qu'il
   * ne recharge pas la page. */
  function upsertLatestRazLocal(raz: ICompteurRaz) {
    if (loadedDate.value != null && raz.dateCompteur.slice(0, 10) !== loadedDate.value) return;
    latestRaz.value = raz;
  }

  function upsertLocal(compteur: ICompteur) {
    // Ignore un push d'un jour différent de celui actuellement chargé (cf. déclaration de
    // `loadedDate`) : sinon une commande créée pour un autre jour pollue le jour affiché.
    // Deux exemptions, toujours acceptées quel que soit le jour affiché :
    // - dateCompteur=null (Stock, U09) : solde courant sans jour.
    // - Nature=Baseline (Prévu) : rattaché au plan de monte, pas au jour (cf. lecture
    //   CompteurService.FindForDayAsync qui filtre Baseline par MountingPlanId, jamais par
    //   DateCompteur) — sinon une mise à jour temps réel du "Prévu" (ex. ajout d'un verrat au
    //   plan) se fait rejeter à tort si le RAZ résolu côté backend porte la date d'un autre jour.
    if (
      loadedDate.value != null &&
      compteur.nature !== 'Baseline' &&
      compteur.dateCompteur != null &&
      compteur.dateCompteur.slice(0, 10) !== loadedDate.value
    ) return;

    const idx = datas.value.findIndex((c) => c.id === compteur.id);
    if (idx > -1) datas.value.splice(idx, 1, compteur);
    else datas.value.unshift(compteur);
    version.value++;
  }

  function removeLocal(id: number) {
    const idx = datas.value.findIndex((c) => c.id === id);
    if (idx > -1) datas.value.splice(idx, 1);
    version.value++;
  }

  // --- U03 : recalcul / RAZ (puis rechargement d'un snapshot cohérent du jour) ---
  /** Recalcul anti-dérive du jour (Besoin/Dispo/Attente/Parc/Stock), sans toucher au Prévu. */
  async function rebuild(date?: string) {
    const result = await CompteurService.rebuild(date, currentCenterId());
    if (!result.error) await load({ date });
    return result;
  }

  /**
   * RAZ : enregistre la trace + les étapes (une par Nature) et lance leur recalcul en tâche de fond
   * côté serveur — répond immédiatement (pas d'attente bloquante du recalcul complet, potentiellement
   * long, cf. analyse RAZ 2026-07-28). L'avancement (LoadingSteps) est piloté par `razSteps`/
   * `razInProgress`, alimentés en temps réel par SignalR ; les compteurs sont rechargés (`load`)
   * automatiquement une fois toutes les étapes terminées (cf. upsertRazStepLocal).
   */
  async function raz(request: ICompteurRazRequest) {
    const result = await CompteurService.raz({
      ...request,
      centerId: request.centerId ?? currentCenterId(),
    });
    if (!result.error && result.data) {
      razDate = request.date;
      razReloaded = false;
      razSteps.value = (result.data as ICompteurRaz).steps ?? [];
    }
    return result;
  }

  /** Pousse une étape de RAZ reçue en temps réel (SignalR) ; recharge les compteurs une fois
   * toutes les étapes du RAZ suivi terminées (Done ou Error). */
  function upsertRazStepLocal(step: ICompteurRazStep) {
    // Ignore les étapes d'un AUTRE RAZ (ex. un autre utilisateur, ou un RAZ non suivi par cet onglet).
    if (razSteps.value.length === 0 || step.razId !== razSteps.value[0].razId) return;

    const idx = razSteps.value.findIndex((s) => s.id === step.id);
    if (idx > -1) razSteps.value.splice(idx, 1, step);
    else razSteps.value.push(step);

    if (razAllStepsSettled.value && razDate && !razReloaded) {
      razReloaded = true;
      load({ date: razDate });
    }
  }

  // --- U05 : potentiel doses d'un verrat (restant à orienter) ---
  /** Charge le potentiel doses d'un verrat pour le centre courant et le cache. */
  async function loadBoarPotential(boarId: number) {
    const result = await CompteurService.getBoarPotential(boarId, currentCenterId());
    if (!result.error) {
      boarPotentials.value = { ...boarPotentials.value, [boarId]: (result.data as number) ?? 0 };
    }
    return result;
  }

  // --- U05 / REQ917 : commandes réservées (même règle que ReservedBoarsBadge — décision US-06 :
  // seules les lignes Besoin issues d'une commande client réservée font foi). ---
  // `sourceId` = id du `ClientOrderRow` réservé : l'orientation (FabricationDosesTile) s'en sert
  // pour router ces commandes vers une ligne « Réservé » dédiée, comme une ligne spécificité.
  const reservedOrderLines = computed(() =>
    datas.value.filter(
      (c) =>
        c.nature === "Besoin" &&
        c.sourceType === "ClientOrderRow" &&
        c.isReserved === true &&
        c.boarId != null
    )
  );

  /** Verrats ayant au moins une commande réservée sur le jour chargé. */
  const reservedBoarIds = computed<Set<number>>(
    () => new Set(reservedOrderLines.value.map((c) => c.boarId as number))
  );

  return {
    datas,
    isLoading,
    loadingMessage,
    version,
    loadVersion,
    load,
    upsertLocal,
    removeLocal,
    // U03 : recalcul / RAZ
    rebuild,
    raz,
    latestRaz,
    loadLatestRaz,
    upsertLatestRazLocal,
    // U03 : avancement du RAZ (panneau de progression par étape)
    razSteps,
    razInProgress,
    razLoadingSteps,
    upsertRazStepLocal,
    // U02 : préférences schémas
    schemaPrefs,
    orderedVisibleSchemaIds,
    loadSchemaPrefs,
    saveSchemaPrefs,
    // U05 : potentiel doses verrat + commandes réservées
    boarPotentials,
    loadBoarPotential,
    reservedOrderLines,
    reservedBoarIds,
    // BLOC JAUNE (attente)
    attenteDosesMono,
    attenteVerratsMono,
    attenteDosesHeteroStd,
    attenteDosesHeteroArm,
    attenteLotsHeteroStd,
    attenteLotsHeteroArm,
    // BLOCS HÉTÉRO (orienté)
    orienteDosesHeteroStd,
    orienteVerratsHeteroStd,
    orienteDosesHeteroArm,
    orienteVerratsHeteroArm,
    orienteDosesHeteroStdAvantLot,
    orienteDosesHeteroArmAvantLot,
    // BLOC PARC
    parcNbVerratPlan,
    parcNbVerratPreleve,
    parcPotentielCommercial,
    parcNbVerratCommercial,
    parcNbVerratNonCommercial,
    // BLOC STOCK
    stockDosesMono,
    stockDosesHeteroStd,
    stockDosesHeteroArm,
    // GETTERS FINS (maille conditionnement / groupe)
    stockDosesByCond,
    orienteHeteroDosesByCond,
    parcNbVerratByGroupe,
  };
});

// Abonnement SignalR : mise à jour temps réel (push de deltas, pas de polling).
signalRManager.connection.on("MajCompteur", (payload: IPayloadStore) => {
  const store = useCompteurStore();
  switch (payload.action) {
    case "Create":
    case "Update": {
      if (payload.data) store.upsertLocal(payload.data as unknown as ICompteur);
      break;
    }
    case "Delete": {
      if (payload.id !== undefined && payload.id !== null) store.removeLocal(payload.id);
      break;
    }
  }
});

// U03 : un autre utilisateur change le plan de monte du jour affiché (RAZ) -> le badge "Plan de
// monte" de tous les clients connectés doit refléter le nouveau plan sans recharger la page.
signalRManager.connection.on("MajCompteurRaz", (payload: IPayloadStore) => {
  const store = useCompteurStore();
  if ((payload.action === "Create" || payload.action === "Update") && payload.data) {
    store.upsertLatestRazLocal(payload.data as unknown as ICompteurRaz);
  }
});

// U03 : avancement d'une étape de RAZ (panneau de progression), poussé au fil de l'eau pendant
// que le recalcul tourne en tâche de fond côté serveur.
signalRManager.connection.on("MajCompteurRazStep", (payload: IPayloadStore) => {
  const store = useCompteurStore();
  if ((payload.action === "Create" || payload.action === "Update") && payload.data) {
    store.upsertRazStepLocal(payload.data as unknown as ICompteurRazStep);
  }
});
